import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

const ACTION_LABELS = {
  csv_import: "CSV import", send_invitations: "Sent invitations", send_batch: "Batch invitations",
  revoke_invitations: "Revoked invitations", test_invitation: "Test invitation", update_settings: "Updated settings",
  purge_seed: "Purged seed data", gdpr_delete: "GDPR delete", save_assessment: "Saved assessment",
  create_course: "Created course", unlock_user: "Unlocked user", create_resource: "Added resource",
  delete_resource: "Deleted resource",
};

export default function AdminAudit() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/admin/audit", { params: { page, page_size: 25 } }).then(({ data }) => setData(data)); }, [page]);

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 1;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">A record of administrator actions for compliance.</p>
      <div className="card-surface overflow-hidden" data-testid="audit-table">
        <div className="hidden grid-cols-[180px_1fr_1fr] gap-3 border-b border-border bg-secondary/50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
          <div>When</div><div>Actor</div><div>Action</div>
        </div>
        <div className="divide-y divide-border">
          {data?.logs.map((l) => (
            <div key={l.id} className="grid grid-cols-1 gap-1 px-5 py-3 text-sm sm:grid-cols-[180px_1fr_1fr] sm:items-center" data-testid={`audit-row-${l.id}`}>
              <div className="text-muted-foreground">{fmt(l.created_at)}</div>
              <div className="truncate">{l.actor_email}</div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#33B6E4]/10 px-2.5 py-0.5 text-xs font-medium text-[#1779AC] dark:text-[#5cc7ef]">
                  <ScrollText className="h-3 w-3" /> {ACTION_LABELS[l.action] || l.action}
                </span>
                {l.detail && <span className="truncate text-xs text-muted-foreground">{l.detail}</span>}
              </div>
            </div>
          ))}
          {data && data.logs.length === 0 && <div className="px-5 py-10 text-center text-sm text-muted-foreground">No actions logged yet.</div>}
        </div>
      </div>
      {data && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tnum">{data.total} entries</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="tnum">Page {page} / {Math.max(1, totalPages)}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
